import React from 'react';
import { Trash2, Upload, ClipboardPaste, X } from 'lucide-react';
import { TeluguInput } from '../../translation/telugu_input';
import { API_BASE_URL } from '@/services/api';

export const EstimationItems = ({ state, actions }) => {
  const { items, setItems, isLocked, discountLimit, setDiscountLimit, isAdminOrOwner } = state;
  const { handleAddRow, handleAddItem, handleDeleteItem, handleSaveDiscountLimit } = actions;

  const readyToFixItems = items.filter(i => i.fixingType === 'Ready to Fix');
  const looseItems = items.filter(i => i.fixingType !== 'Ready to Fix');

  const renderItemCard = (item) => {
    if (item.type === 'row') {
      return (
        <div key={item.id} className="relative group flex items-center gap-2 my-2 py-2 border-b-2 border-slate-200">
          <input
            type="text"
            className="flex-1 bg-transparent text-slate-800 font-medium text-[15px] outline-none placeholder:text-slate-400 capitalize"
            placeholder="Enter Section Heading (e.g. Accessories)"
            value={item.text || ''}
            onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, text: e.target.value } : i))}
            readOnly={isLocked}
          />
          <button
            onClick={() => handleDeleteItem(item.id)}
            disabled={isLocked}
            className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all disabled:opacity-0"
            title="Delete row"
          >
            <Trash2 size={16} />
          </button>
        </div>
      );
    }

    const amount = (Number(item.qty) || 0) * (Number(item.rate) || 0);
    const isEditable = !item.fromStock || item.isSpecialItem;

    return (
      <div key={item.id} className="border border-slate-200 rounded-xl p-5 shadow-sm bg-white relative group">
        <button
          onClick={() => handleDeleteItem(item.id)}
          disabled={isLocked}
          className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors z-10 disabled:hidden"
          title="Delete item"
        >
          <Trash2 size={16} />
        </button>

        {item.fromStock && (
          <div className="absolute top-0 right-12 mt-4 mr-2">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 uppercase tracking-wider">
              Stock
            </span>
          </div>
        )}

        <div className="flex flex-row gap-3 mb-3 pr-2">
          <div className="w-24 shrink-0 flex flex-col justify-start">
            <label className="flex items-center gap-1.5 cursor-pointer bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-all select-none p-1.5 w-max mb-2 mt-5">
              <input
                type="checkbox"
                className="w-3 h-3 text-[#C91D25] border-slate-300 rounded focus:ring-[#C91D25] cursor-pointer"
                checked={item.isSpecialItem}
                disabled={isLocked}
                onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, isSpecialItem: e.target.checked } : i))}
              />
              <span className="text-[10px] font-bold text-slate-700 whitespace-nowrap">Special</span>
            </label>
            <div
              className="relative w-full border-2 border-dashed border-slate-200 rounded-lg p-1.5 hover:border-[#C91D25]/50 transition-colors bg-slate-50/50 outline-none min-h-[90px] flex items-center justify-center group"
              tabIndex={isLocked ? -1 : 0}
              onPaste={(e) => {
                if (isLocked) return;
                const clipItems = e.clipboardData?.items;
                if (!clipItems) return;
                for (let i = 0; i < clipItems.length; i++) {
                  if (clipItems[i].type.indexOf('image') !== -1) {
                    const file = clipItems[i].getAsFile();
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => setItems(items.map(it => it.id === item.id ? { ...it, image: reader.result } : it));
                      reader.readAsDataURL(file);
                    }
                    break;
                  }
                }
              }}
            >
              {!item.image ? (
                <div className="flex flex-col items-center justify-center gap-1">
                   <span className="text-[9px] text-slate-400">Ctrl+V paste</span>
                   <div className="flex gap-1.5 mt-1">
                     <label className={`flex items-center justify-center p-1.5 bg-slate-200 text-slate-700 rounded shadow-sm ${isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-300'}`} title="Upload Image">
                       <Upload size={12} />
                       <input
                         type="file"
                         accept="image/*"
                         className="hidden"
                         disabled={isLocked}
                         onChange={(e) => {
                           const file = e.target.files[0];
                           if (file) {
                             const reader = new FileReader();
                             reader.onloadend = () => setItems(items.map(it => it.id === item.id ? { ...it, image: reader.result } : it));
                             reader.readAsDataURL(file);
                           }
                         }}
                       />
                     </label>
                     <button
                        type="button"
                        title="Paste Image from Clipboard"
                        disabled={isLocked}
                        onClick={async (e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          try {
                            const clipboardItems = await navigator.clipboard.read();
                            for (const clipboardItem of clipboardItems) {
                              for (const type of clipboardItem.types) {
                                if (type.startsWith('image/')) {
                                  const blob = await clipboardItem.getType(type);
                                  const reader = new FileReader();
                                  reader.onloadend = () => setItems(items.map(it => it.id === item.id ? { ...it, image: reader.result } : it));
                                  reader.readAsDataURL(blob);
                                  return;
                                }
                              }
                            }
                          } catch (err) {}
                        }}
                        className={`flex items-center justify-center p-1.5 bg-slate-200 text-slate-700 rounded shadow-sm ${isLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-300'}`}
                      >
                        <ClipboardPaste size={12} />
                      </button>
                   </div>
                </div>
              ) : (
                <div className="relative w-full h-full flex justify-center">
                  <img src={item.image.startsWith('data:') || item.image.startsWith('http') ? item.image : `${API_BASE_URL}/api/inventory/settings/images/${item.image}`} alt="preview" className="max-h-[70px] object-contain rounded" />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setItems(items.map(it => it.id === item.id ? { ...it, image: null } : it));
                    }}
                    disabled={isLocked}
                    className={`absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 transition-opacity shadow-sm hover:bg-red-600 ${isLocked ? 'hidden' : 'opacity-0 group-hover:opacity-100'}`}
                    title="Remove image"
                  >
                    <X size={10} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-center gap-2 mt-4">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className="block text-[11px] font-semibold text-slate-500 mb-0.5">Item</label>
                <input
                  type="text"
                  className={`w-full h-8 px-2 rounded-md border text-[12px] font-medium focus:outline-none transition-all ${isEditable ? 'border-slate-200 text-slate-800 focus:border-slate-400 bg-white' : 'border-slate-100 text-slate-600 bg-slate-50 cursor-not-allowed'}`}
                  value={item.product}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, product: e.target.value } : i))}
                  placeholder="Item name"
                  readOnly={!isEditable}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-[11px] font-semibold text-slate-500 mb-0.5">Color</label>
                <input
                  type="text"
                  className={`w-full h-8 px-2 rounded-md border text-[12px] font-medium focus:outline-none transition-all ${isEditable ? 'border-slate-200 text-slate-800 focus:border-slate-400 bg-white' : 'border-slate-100 text-slate-600 bg-slate-50 cursor-not-allowed'}`}
                  value={item.color}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, color: e.target.value } : i))}
                  placeholder="Color"
                  readOnly={!isEditable}
                />
              </div>

              <div className="col-span-1">
                <label className="block text-[11px] font-semibold text-slate-500 mb-0.5">Profile</label>
                <input
                  type="text"
                  className={`w-full h-8 px-2 rounded-md border text-[12px] font-medium focus:outline-none transition-all ${isEditable ? 'border-slate-200 text-slate-800 focus:border-slate-400 bg-white' : 'border-slate-100 text-slate-600 bg-slate-50 cursor-not-allowed'}`}
                  value={item.profile}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, profile: e.target.value } : i))}
                  placeholder="Profile"
                  readOnly={!isEditable}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-[11px] font-semibold text-slate-500 mb-0.5" title="Width (ft)">W (ft)</label>
                <input
                  type="text"
                  className={`w-full h-8 px-2 rounded-md border text-[12px] font-medium focus:outline-none transition-all ${isEditable ? 'border-slate-200 text-slate-800 focus:border-slate-400 bg-white' : 'border-slate-100 text-slate-600 bg-slate-50 cursor-not-allowed'}`}
                  value={item.width}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, width: e.target.value } : i))}
                  readOnly={!isEditable}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-[11px] font-semibold text-slate-500 mb-0.5" title="Height (ft)">H (ft)</label>
                <input
                  type="text"
                  className={`w-full h-8 px-2 rounded-md border text-[12px] font-medium focus:outline-none transition-all ${isEditable ? 'border-slate-200 text-slate-800 focus:border-slate-400 bg-white' : 'border-slate-100 text-slate-600 bg-slate-50 cursor-not-allowed'}`}
                  value={item.height}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, height: e.target.value } : i))}
                  readOnly={!isEditable}
                />
              </div>

              <div className="col-span-1">
                <label className="block text-[11px] font-semibold text-slate-500 mb-0.5" title="Thickness">Thick</label>
                <input
                  type="text"
                  className={`w-full h-8 px-2 rounded-md border text-[12px] font-medium focus:outline-none transition-all ${isEditable ? 'border-slate-200 text-slate-800 focus:border-slate-400 bg-white' : 'border-slate-100 text-slate-600 bg-slate-50 cursor-not-allowed'}`}
                  value={item.thickness || ''}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, thickness: e.target.value } : i))}
                  readOnly={!isEditable}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-[11px] font-semibold text-slate-500 mb-0.5">Qty</label>
                <input
                  type="number"
                  className="w-full h-8 px-2 rounded-md border border-slate-200 text-slate-800 text-[12px] font-medium focus:outline-none focus:border-slate-400 transition-all"
                  value={item.qty}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, qty: e.target.value } : i))}
                />
              </div>
              <div className="col-span-1">
                <label className="block text-[11px] font-semibold text-slate-500 mb-0.5">Price</label>
                <input
                  type="number"
                  className={`w-full h-8 px-2 rounded-md border text-[12px] font-medium focus:outline-none transition-all ${isEditable ? 'border-slate-200 text-slate-800 focus:border-slate-400 bg-white' : 'border-slate-100 text-slate-600 bg-slate-50 cursor-not-allowed'}`}
                  value={item.rate}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, rate: e.target.value } : i))}
                  readOnly={!isEditable}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4 pr-8">
          <div className="flex items-center justify-between mb-1">
            <label className="block text-[12px] font-medium text-slate-500">Description</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-[11px] font-medium text-slate-500">Translate to Telugu</span>
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={item.isTeluguTranslation !== false}
                  onChange={(e) => setItems(items.map(i => i.id === item.id ? { ...i, isTeluguTranslation: e.target.checked } : i))}
                />
                <div className="w-7 h-4 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#C91D25]"></div>
              </div>
            </label>
          </div>
          <TeluguInput
            value={item.description || ''}
            onChange={(text) => setItems(items.map(i => i.id === item.id ? { ...i, description: text } : i))}
            placeholder="Add an optional description (English to Telugu transliteration supported)..."
            className="w-full h-9 px-2.5 rounded-lg border border-slate-200 text-slate-800 text-[13px] font-medium focus:outline-none focus:border-slate-400 transition-all"
            enabled={item.isTeluguTranslation !== false}
          />
        </div>

        <div className="bg-slate-50/80 rounded-lg p-3 flex items-center justify-between border border-slate-100">
          <div>
            <div className="text-[16px] font-bold text-slate-900 tracking-tight">
              ₹{amount.toLocaleString('en-IN')}
            </div>
          </div>
          {item.fromStock && (
            <div className="text-right">
              <div className="text-[10px] text-slate-400 font-medium mb-1 pr-1">Availability</div>
              <div className="px-3 py-1 bg-[#fef3c7] text-[#92400e] rounded-full text-[11px] font-bold tracking-wide">
                {item.stockQty} in stock
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-8">
        {readyToFixItems.length > 0 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center px-1">
              <h3 className="font-bold text-slate-800 text-sm tracking-wide capitalize">Ready to Fix</h3>
              <div className="flex items-center gap-4">
                {!isLocked && (
                  <>
                    <button
                      onClick={() => handleAddRow('Ready to Fix')}
                      className="text-[11px] font-bold text-slate-500 hover:text-slate-800 tracking-wider hover:underline flex items-center gap-1"
                    >
                      + Add Row
                    </button>
                    <button
                      onClick={() => handleAddItem('Ready to Fix')}
                      className="text-[11px] font-bold text-[#C91D25] hover:text-[#a8161d] tracking-wider hover:underline flex items-center gap-1"
                    >
                      + Add Item
                    </button>
                  </>
                )}
              </div>
            </div>
            {readyToFixItems.map(renderItemCard)}
          </div>
        )}

        {looseItems.length > 0 && (
          <div className="space-y-4">
            <div className="flex justify-between items-center px-1">
              <h3 className="font-bold text-slate-800 text-sm tracking-wide capitalize">Loose Items</h3>
              <div className="flex items-center gap-4">
                {!isLocked && (
                  <>
                    <button
                      onClick={() => handleAddRow('Loose')}
                      className="text-[11px] font-bold text-slate-500 hover:text-slate-800 tracking-wider hover:underline flex items-center gap-1"
                    >
                      + Add Row
                    </button>
                    <button
                      onClick={() => handleAddItem('Loose')}
                      className="text-[11px] font-bold text-[#C91D25] hover:text-[#a8161d] tracking-wider hover:underline flex items-center gap-1"
                    >
                      + Add Item
                    </button>
                  </>
                )}
              </div>
            </div>
            {looseItems.map(renderItemCard)}
          </div>
        )}
      </div>
    </div>
  );
};
